/**
 * ReportTable: a ranked report table. Sortable headers, numbers right-aligned in tabular figures, an inline bar in the
 * first numeric column, a cell with a drill opens the cases behind it. A column the viewer may not see is not
 * rendered; a missing cell says why in words (never 0). Scrolls horizontally inside its card, never the page.
 * Owner: slice web-charts (#1115).
 */
import { cn } from '@patchbay/ui'
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react'
import * as React from 'react'
import type { Cell, Column, Row, Table } from '../../../lib/api/generated'
import { cellNumber, DASH, formatCount, formatValue, isVisible, slotStripped, slotValue, type Visibility } from './format'
import type { OnDrill } from './kpi'
import { STATE_LABEL, StateText } from './state-text'

type Sort = { key: string; dir: 'asc' | 'desc' } | null

const SHORT_STATE = { insufficient: 'Too few', not_tracked: 'Not tracked', unavailable: "Couldn't load" } as const

export function ReportTable({ table, onDrill, canSee }: { table: Table; onDrill?: OnDrill; canSee?: (v: Visibility) => boolean }) {
  const rows = table.rows ?? []
  const columns = table.columns.filter((c) => {
    const stripped =
      (c.visibility === 'money' || c.visibility === 'cost') &&
      rows.length > 0 &&
      rows.every((r) => {
        const cell = r.cells?.find((x) => x.key === c.key)
        return !cell || ((cell.state ?? 'ok') === 'ok' && slotStripped(cell, c.visibility))
      })
    return isVisible(c.visibility, canSee, stripped)
  })
  const [sort, setSort] = React.useState<Sort>(null)
  const cellOf = React.useCallback((r: Row, key: string): Cell | undefined => r.cells?.find((c) => c.key === key), [])
  const barCol = columns.find((c) => c.unit != null)
  const barMax = React.useMemo(() => {
    if (!barCol) return 0
    let m = 0
    for (const r of rows) {
      const c = cellOf(r, barCol.key)
      const v = c ? cellNumber(c, barCol) : null
      if (v !== null) m = Math.max(m, Math.abs(v))
    }
    return m
  }, [rows, barCol, cellOf])

  const sorted = React.useMemo(() => {
    if (!sort) return rows
    const col = sort.key === '__label' ? null : columns.find((c) => c.key === sort.key)
    const dir = sort.dir === 'asc' ? 1 : -1
    return [...rows].sort((a, b) => {
      if (!col) return a.label.localeCompare(b.label) * dir
      if (col.unit == null) {
        const ta = cellOf(a, col.key)?.text ?? ''
        const tb = cellOf(b, col.key)?.text ?? ''
        return ta.localeCompare(tb) * dir
      }
      const ca = cellOf(a, col.key)
      const cb = cellOf(b, col.key)
      const va = ca ? cellNumber(ca, col) : null
      const vb = cb ? cellNumber(cb, col) : null
      // Missing values sort last in either direction: absence is not the smallest number.
      if (va === null && vb === null) return 0
      if (va === null) return 1
      if (vb === null) return -1
      return (va - vb) * dir
    })
  }, [rows, sort, columns, cellOf])

  const toggle = (key: string, numeric: boolean) =>
    setSort((s) => {
      const first = numeric ? 'desc' : 'asc'
      if (!s || s.key !== key) return { key, dir: first }
      if (s.dir === first) return { key, dir: first === 'desc' ? 'asc' : 'desc' }
      return null
    })

  const state = table.state ?? 'ok'
  return (
    <section className="reports-root min-w-0 rounded-lg border border-border bg-card" data-report-table={table.key}>
      <header className="flex items-baseline justify-between gap-2 border-b border-border px-3 py-2">
        <h3 className="text-sm font-semibold text-foreground">{table.title}</h3>
        {state === 'ok' && table.total != null && table.total > rows.length ? (
          <span className="tabular text-xs text-muted-foreground">
            Top {formatCount(rows.length)} of {formatCount(table.total)}
          </span>
        ) : null}
      </header>
      {state !== 'ok' ? (
        <div className="px-3 py-6">
          <StateText state={state} note={table.note} />
        </div>
      ) : rows.length === 0 ? (
        <p className="px-3 py-6 text-sm text-muted-foreground">Nothing matched in this range.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead className="bg-muted text-xs text-muted-foreground">
              <tr>
                <SortHeader label="Name" active={sort?.key === '__label' ? sort.dir : null} onClick={() => toggle('__label', false)} />
                {columns.map((c) => (
                  <SortHeader
                    key={c.key}
                    label={c.label}
                    numeric={c.unit != null}
                    active={sort?.key === c.key ? sort.dir : null}
                    onClick={() => toggle(c.key, c.unit != null)}
                  />
                ))}
              </tr>
            </thead>
            <tbody>
              {sorted.map((r) => (
                <tr key={r.key} className="border-t border-border" data-row={r.key}>
                  <th scope="row" className="max-w-[16rem] truncate px-3 py-1.5 text-left font-normal text-foreground">
                    {r.href ? (
                      <a href={r.href} className="hover:underline">
                        {r.label}
                      </a>
                    ) : (
                      r.label
                    )}
                  </th>
                  {columns.map((c) => (
                    <TableCell
                      key={c.key}
                      column={c}
                      cell={cellOf(r, c.key)}
                      bar={c === barCol ? barMax : 0}
                      onDrill={onDrill}
                      rowLabel={r.label}
                    />
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {state === 'ok' && table.note ? <p className="border-t border-border px-3 py-2 text-xs text-muted-foreground">{table.note}</p> : null}
    </section>
  )
}

function SortHeader({
  label,
  numeric = false,
  active,
  onClick,
}: {
  label: string
  numeric?: boolean
  active: 'asc' | 'desc' | null
  onClick: () => void
}) {
  const Icon = active === 'asc' ? ArrowUp : active === 'desc' ? ArrowDown : ArrowUpDown
  return (
    <th
      scope="col"
      aria-sort={active === 'asc' ? 'ascending' : active === 'desc' ? 'descending' : 'none'}
      className={cn('px-3 py-1.5 font-medium whitespace-nowrap', numeric ? 'text-right' : 'text-left')}
    >
      <button
        type="button"
        onClick={onClick}
        className={cn('inline-flex items-center gap-1 hover:text-foreground', numeric && 'flex-row-reverse')}
      >
        {label}
        <Icon aria-hidden className={cn('size-3', active ? 'opacity-100' : 'opacity-40')} />
      </button>
    </th>
  )
}

function TableCell({
  column,
  cell,
  bar,
  onDrill,
  rowLabel,
}: {
  column: Column
  cell: Cell | undefined
  bar: number
  onDrill?: OnDrill
  rowLabel: string
}) {
  if (column.unit == null) {
    return <td className="px-3 py-1.5 text-left text-foreground">{cell?.text ?? DASH}</td>
  }
  const state = cell?.state ?? 'ok'
  if (!cell || state !== 'ok') {
    const s = state === 'ok' ? 'unavailable' : state
    return (
      <td className="px-3 py-1.5 text-right text-xs whitespace-nowrap text-muted-foreground" data-state={s} title={STATE_LABEL[s]}>
        {cell ? SHORT_STATE[s] : DASH}
      </td>
    )
  }
  const raw = slotValue(cell, column.visibility)
  const n = cellNumber(cell, column)
  const text = n === null ? 'no data' : formatValue(column.unit, raw)
  const width = bar > 0 && n !== null ? Math.max(2, (Math.abs(n) / bar) * 100) : 0
  const content = (
    <span className="flex items-center justify-end gap-2">
      {bar > 0 ? (
        <span aria-hidden className="hidden h-1.5 w-16 shrink-0 overflow-hidden rounded-full bg-muted sm:flex">
          {width > 0 ? (
            <span data-inline-bar className="h-full rounded-full" style={{ width: `${width}%`, background: 'var(--chart-1)' }} />
          ) : null}
        </span>
      ) : null}
      <span className={cn('tabular', n === null ? 'text-muted-foreground' : 'text-foreground')}>{text}</span>
    </span>
  )
  const drill = cell.drill
  return (
    <td className="px-3 py-1.5 text-right whitespace-nowrap">
      {drill && onDrill && n !== null ? (
        <button
          type="button"
          data-cell-drill={column.key}
          className="w-full rounded-sm hover:underline focus-visible:outline-2 focus-visible:outline-ring"
          onClick={() => onDrill(drill)}
          aria-label={`${rowLabel}, ${column.label}: ${text}. Open the cases behind it`}
        >
          {content}
        </button>
      ) : (
        content
      )}
    </td>
  )
}
